import { GRUPOS, nomeGrupo } from '@precificador/shared/pricing.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { FileUp, History, PackagePlus, Search } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { ErroApi } from '../api/cliente';
import { custos as apiCustos } from '../api/endpoints';
import type { LinhaImportacao, MaterialCusto, StatusImportacao } from '../api/tipos';
import { Aviso } from '../components/Aviso';
import { Botao } from '../components/Botao';
import { CaixaMarcar, Campo, Selecao, estiloEntrada } from '../components/Campo';
import { Dialogo } from '../components/Dialogo';
import { EntradaNumero } from '../components/EntradaNumero';
import { Carregando, Pagina } from '../components/Pagina';
import { formatarCusto, formatarDataHora, formatarInteiro, formatarPeso, lerNumero, plural } from '../lib/formatos';

export const CHAVE_CUSTOS = ['custos'];

function semAcento(s: string) {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function Custos() {
  const cliente = useQueryClient();
  const { data, isPending, error } = useQuery({ queryKey: CHAVE_CUSTOS, queryFn: apiCustos.listar });
  const [grupo, setGrupo] = useState('');
  const [busca, setBusca] = useState('');
  const [soSemCusto, setSoSemCusto] = useState(false);
  const [soSemPeso, setSoSemPeso] = useState(false);
  const [historico, setHistorico] = useState<MaterialCusto | null>(null);
  const [importando, setImportando] = useState(false);
  const [mensagem, setMensagem] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);

  const salvar = useMutation({
    mutationFn: ({ codigo, custoKg }: { codigo: string; custoKg: number | null }) => apiCustos.salvar(codigo, custoKg),
    onSuccess: ({ custo }) => {
      cliente.setQueryData<{ materiais: MaterialCusto[] }>(CHAVE_CUSTOS, (antes) =>
        antes ? { materiais: antes.materiais.map((m) => (m.cdMaterial === custo.cdMaterial ? { ...m, ...custo } : m)) } : antes,
      );
      setMensagem(null);
    },
    onError: (e) => setMensagem({ tipo: 'erro', texto: e instanceof ErroApi ? e.message : 'Não foi possível salvar o custo.' }),
  });

  const salvarAcrescimo = useMutation({
    mutationFn: ({ codigo, valor }: { codigo: string; valor: number | null }) => apiCustos.salvarAcrescimo(codigo, valor),
    onSuccess: ({ acrescimo }) => {
      cliente.setQueryData<{ materiais: MaterialCusto[] }>(CHAVE_CUSTOS, (antes) =>
        antes
          ? { materiais: antes.materiais.map((m) => (m.cdMaterial === acrescimo.cdMaterial ? { ...m, acrescimoUnit: acrescimo.acrescimoUnit } : m)) }
          : antes,
      );
      setMensagem(null);
    },
    onError: (e) => setMensagem({ tipo: 'erro', texto: e instanceof ErroApi ? e.message : 'Não foi possível salvar o acréscimo.' }),
  });
  const [acrescimoLote, setAcrescimoLote] = useState(false);

  const materiais = data?.materiais ?? [];
  const filtrados = useMemo(() => {
    const termo = semAcento(busca.trim());
    return materiais.filter(
      (m) =>
        (!grupo || m.grupo === grupo) &&
        (!soSemCusto || m.custoKg === null) &&
        (!soSemPeso || !(m.peso && m.peso > 0)) &&
        (!termo || semAcento(m.cdMaterial).includes(termo) || semAcento(m.descricao).includes(termo)),
    );
  }, [materiais, grupo, busca, soSemCusto, soSemPeso]);

  const semCusto = materiais.filter((m) => m.custoKg === null).length;
  const semPeso = materiais.filter((m) => !(m.peso && m.peso > 0)).length;

  return (
    <Pagina
      titulo="Custos"
      larga
      contexto={
        data
          ? `${plural(materiais.length, 'material', 'materiais')} dos grupos precificados · ${formatarInteiro(semCusto)} sem custo · ${formatarInteiro(semPeso)} sem peso no CIGAM`
          : 'Custo por kg de cada material, usado em todas as tabelas.'
      }
      acoes={
        <Botao variante="secundario" onClick={() => setImportando(true)}>
          <FileUp aria-hidden className="size-4" /> Importar planilha
        </Botao>
      }
    >
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <Selecao rotulo="Grupo" value={grupo} onChange={(e) => setGrupo(e.target.value)} className="w-52">
          <option value="">Todos os grupos</option>
          {GRUPOS.map((g) => (
            <option key={g.codigo} value={g.codigo}>
              {g.nome}
            </option>
          ))}
        </Selecao>
        <div className="flex w-72 flex-col gap-1.5">
          <label htmlFor="busca-custo" className="text-[13px] leading-[18px] font-medium">
            Buscar
          </label>
          <div className="relative">
            <Search aria-hidden className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-tinta-2" />
            <input
              id="busca-custo"
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Código ou descrição"
              className={clsx(estiloEntrada, 'border-linha pl-8')}
            />
          </div>
        </div>
        <CaixaMarcar rotulo="Somente sem custo" checked={soSemCusto} onChange={(e) => setSoSemCusto(e.target.checked)} className="mb-2" />
        <CaixaMarcar rotulo="Somente sem peso" checked={soSemPeso} onChange={(e) => setSoSemPeso(e.target.checked)} className="mb-2" />
        {data && (
          <div className="mb-1 ml-auto flex items-center gap-3">
            <p className="text-[13px] text-tinta-2">
              Mostrando {formatarInteiro(filtrados.length)} de {formatarInteiro(materiais.length)}
            </p>
            <Botao variante="secundario" tamanho="compacto" disabled={filtrados.length === 0} onClick={() => setAcrescimoLote(true)}>
              <PackagePlus aria-hidden className="size-3.5" /> Acréscimo nos filtrados
            </Botao>
          </div>
        )}
      </div>

      {mensagem && (
        <Aviso tipo={mensagem.tipo} className="mb-3">
          {mensagem.texto}
        </Aviso>
      )}

      {isPending ? (
        <Carregando texto="Lendo os materiais no CIGAM…" />
      ) : error ? (
        <Aviso tipo="erro">{error.message}</Aviso>
      ) : (
        <div className="overflow-x-auto rounded-painel border border-linha bg-superficie">
          <table className="w-full min-w-[1100px] border-collapse text-left text-[13px]">
            <thead className="sticky top-0 z-10 border-b border-linha bg-superficie text-[12px] text-tinta-2">
              <tr>
                <th className="px-3 py-2 font-medium">Código</th>
                <th className="px-3 py-2 font-medium">Descrição</th>
                <th className="px-3 py-2 font-medium">EAN</th>
                <th className="px-3 py-2 font-medium">Grupo</th>
                <th className="px-3 py-2 text-right font-medium">Peso (kg)</th>
                <th className="w-36 px-3 py-2 text-right font-medium">Custo/kg (R$)</th>
                <th className="w-32 px-3 py-2 text-right font-medium" title="Somado ao preço unitário depois do cálculo, sem margem nem custos variáveis">
                  Acréscimo/un (R$)
                </th>
                <th className="px-3 py-2 font-medium">Atualizado</th>
                <th className="px-3 py-2">
                  <span className="sr-only">Histórico</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((m) => (
                <tr key={m.cdMaterial} className="border-b border-linha-suave last:border-0 hover:bg-fundo/60">
                  <td className="px-3 py-1 font-mono">{m.cdMaterial}</td>
                  <td className="px-3 py-1">{m.descricao}</td>
                  <td className="px-3 py-1 font-mono text-tinta-2">{m.ean ?? '—'}</td>
                  <td className="px-3 py-1 whitespace-nowrap text-tinta-2">{nomeGrupo(m.grupo)}</td>
                  <td className={clsx('numero px-3 py-1 text-right', !(m.peso && m.peso > 0) && 'font-medium text-erro')}>
                    {m.peso && m.peso > 0 ? formatarPeso(m.peso) : 'sem peso'}
                  </td>
                  <td className="px-3 py-1">
                    <EntradaNumero
                      rotulo={`Custo por kg de ${m.cdMaterial}`}
                      valor={m.custoKg}
                      permitirVazio
                      placeholder="sem custo"
                      aoConfirmar={(custoKg) => salvar.mutateAsync({ codigo: m.cdMaterial, custoKg })}
                    />
                  </td>
                  <td className="px-3 py-1">
                    <EntradaNumero
                      rotulo={`Acréscimo por unidade de ${m.cdMaterial}`}
                      valor={m.acrescimoUnit}
                      casas={4}
                      permitirVazio
                      placeholder="—"
                      destaque={m.acrescimoUnit !== null}
                      aoConfirmar={(valor) => salvarAcrescimo.mutateAsync({ codigo: m.cdMaterial, valor: valor === 0 ? null : valor })}
                    />
                  </td>
                  <td className="px-3 py-1 whitespace-nowrap text-tinta-2">
                    {m.atualizadoEm ? (
                      <>
                        {formatarDataHora(m.atualizadoEm)}
                        {m.atualizadoPor && <span className="block text-[12px]">{m.atualizadoPor}</span>}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-2 py-1 text-right">
                    <Botao tamanho="compacto" variante="discreto" onClick={() => setHistorico(m)} aria-label={`Histórico de ${m.cdMaterial}`}>
                      <History aria-hidden className="size-3.5" /> Histórico
                    </Botao>
                  </td>
                </tr>
              ))}
              {filtrados.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-tinta-2">
                    Nenhum material com esses filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialogo
        aberto={historico !== null}
        titulo="Histórico do custo"
        descricao={historico ? `${historico.cdMaterial} — ${historico.descricao}` : undefined}
        aoFechar={() => setHistorico(null)}
      >
        {historico && <Historico codigo={historico.cdMaterial} />}
      </Dialogo>

      <Dialogo aberto={acrescimoLote} titulo="Acréscimo por unidade" largura="max-w-md" aoFechar={() => setAcrescimoLote(false)}>
        {acrescimoLote && (
          <AcrescimoLote
            codigos={filtrados.map((m) => m.cdMaterial)}
            aoConcluir={(texto) => {
              setAcrescimoLote(false);
              setMensagem({ tipo: 'ok', texto });
              void cliente.invalidateQueries({ queryKey: CHAVE_CUSTOS });
            }}
          />
        )}
      </Dialogo>

      <Dialogo aberto={importando} titulo="Importar custos" largura="max-w-3xl" aoFechar={() => setImportando(false)}>
        {importando && (
          <Importacao
            aoConcluir={(texto) => {
              setImportando(false);
              setMensagem({ tipo: 'ok', texto });
              void cliente.invalidateQueries({ queryKey: CHAVE_CUSTOS });
            }}
          />
        )}
      </Dialogo>
    </Pagina>
  );
}

function Historico({ codigo }: { codigo: string }) {
  const { data, isPending, error } = useQuery({ queryKey: ['custos', codigo, 'historico'], queryFn: () => apiCustos.historico(codigo) });
  if (isPending) return <Carregando />;
  if (error) return <Aviso tipo="erro">{error.message}</Aviso>;
  if (data.historico.length === 0) return <p className="text-tinta-2">Nenhuma alteração registrada.</p>;
  return (
    <table className="w-full text-left text-[13px]">
      <thead className="border-b border-linha text-[12px] text-tinta-2">
        <tr>
          <th className="py-2 pr-3 font-medium">Quando</th>
          <th className="py-2 pr-3 font-medium">Campo</th>
          <th className="py-2 pr-3 text-right font-medium">Anterior</th>
          <th className="py-2 pr-3 text-right font-medium">Novo</th>
          <th className="py-2 pr-3 font-medium">Quem</th>
          <th className="py-2 font-medium">Origem</th>
        </tr>
      </thead>
      <tbody>
        {data.historico.map((h) => (
          <tr key={h.id} className="border-b border-linha-suave last:border-0">
            <td className="py-1.5 pr-3 whitespace-nowrap">{formatarDataHora(h.alteradoEm)}</td>
            <td className="py-1.5 pr-3 whitespace-nowrap">{h.campo === 'ACRESCIMO_UNIT' ? 'Acréscimo/un' : 'Custo/kg'}</td>
            <td className="numero py-1.5 pr-3 text-right text-tinta-2">{formatarCusto(h.valorAnterior)}</td>
            <td className="numero py-1.5 pr-3 text-right font-medium">{h.valorNovo === null ? 'removido' : formatarCusto(h.valorNovo)}</td>
            <td className="py-1.5 pr-3">{h.alteradoPor ?? '—'}</td>
            <td className="py-1.5">{h.origem === 'IMPORTACAO' ? 'Importação' : h.origem === 'LOTE' ? 'Em lote' : 'Manual'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const ROTULO_STATUS: Record<StatusImportacao, { texto: string; classe: string }> = {
  novo: { texto: 'Novo', classe: 'text-ok' },
  alterado: { texto: 'Alterado', classe: 'text-acao-escura' },
  igual: { texto: 'Sem mudança', classe: 'text-tinta-2' },
  nao_encontrado: { texto: 'Não encontrado', classe: 'text-erro' },
  invalido: { texto: 'Inválido', classe: 'text-erro' },
  duplicado: { texto: 'Repetido', classe: 'text-aviso' },
};

function Importacao({ aoConcluir }: { aoConcluir: (mensagem: string) => void }) {
  const entrada = useRef<HTMLInputElement>(null);
  const [linhas, setLinhas] = useState<LinhaImportacao[] | null>(null);
  const [arquivo, setArquivo] = useState<string>('');
  const [erro, setErro] = useState<string | null>(null);
  const [lendo, setLendo] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [temAcrescimo, setTemAcrescimo] = useState(false);

  async function escolher(f: File | undefined) {
    if (!f) return;
    setErro(null);
    setLendo(true);
    try {
      const r = await apiCustos.previaImportacao(f);
      setLinhas(r.itens);
      setArquivo(r.arquivo);
      setTemAcrescimo(r.temAcrescimo);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível ler o arquivo.');
      setLinhas(null);
    } finally {
      setLendo(false);
      if (entrada.current) entrada.current.value = '';
    }
  }

  const aGravar = (linhas ?? []).filter((l) => l.status === 'novo' || l.status === 'alterado');
  const comProblema = (linhas ?? []).filter((l) => l.status === 'nao_encontrado' || l.status === 'invalido' || l.status === 'duplicado');

  async function confirmar() {
    setGravando(true);
    setErro(null);
    try {
      const r = await apiCustos.confirmarImportacao(
        aGravar.map((l) => ({ codigo: l.codigo, custoKg: l.custoNovo!, ...(temAcrescimo ? { acrescimoUnit: l.acrescimoNovo ?? null } : {}) })),
      );
      aoConcluir(`Importação concluída: ${plural(r.gravados, 'custo gravado', 'custos gravados')}.`);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível gravar.');
    } finally {
      setGravando(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-tinta-2">
        Arquivo <strong className="text-tinta">.xlsx</strong> (primeira aba) ou <strong className="text-tinta">.csv</strong> com as colunas{' '}
        <code className="rounded bg-fundo px-1 font-mono">codigo;custo_kg</code>. Coluna opcional{' '}
        <code className="rounded bg-fundo px-1 font-mono">acrescimo</code> (R$ por unidade; vazio = sem acréscimo). Sem essa coluna, os acréscimos
        cadastrados não mudam. Nada é gravado antes da sua confirmação.
      </p>
      <div className="flex items-center gap-3">
        <input ref={entrada} type="file" accept=".csv,.xlsx,text/csv" className="hidden" onChange={(e) => void escolher(e.target.files?.[0])} />
        <Botao variante="secundario" carregando={lendo} onClick={() => entrada.current?.click()}>
          <FileUp aria-hidden className="size-4" /> {linhas ? 'Escolher outro arquivo' : 'Escolher arquivo'}
        </Botao>
        {arquivo && <span className="text-tinta-2">{arquivo}</span>}
      </div>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}

      {linhas && (
        <>
          <p>
            {plural(linhas.length, 'linha')} lidas · <strong>{plural(aGravar.length, 'custo', 'custos')} a gravar</strong>
            {comProblema.length > 0 && <span className="text-erro"> · {plural(comProblema.length, 'linha ignorada', 'linhas ignoradas')}</span>}
          </p>
          <div className="max-h-[45vh] overflow-y-auto rounded-painel border border-linha">
            <table className="w-full text-left text-[13px]">
              <thead className="sticky top-0 border-b border-linha bg-superficie text-[12px] text-tinta-2">
                <tr>
                  <th className="px-2 py-1.5 font-medium">Linha</th>
                  <th className="px-2 py-1.5 font-medium">Código</th>
                  <th className="px-2 py-1.5 font-medium">Descrição</th>
                  <th className="px-2 py-1.5 text-right font-medium">Atual</th>
                  <th className="px-2 py-1.5 text-right font-medium">Novo</th>
                  {temAcrescimo && (
                    <>
                      <th className="px-2 py-1.5 text-right font-medium">Acrésc. atual</th>
                      <th className="px-2 py-1.5 text-right font-medium">Acrésc. novo</th>
                    </>
                  )}
                  <th className="px-2 py-1.5 font-medium">Situação</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => (
                  <tr key={l.linha} className="border-b border-linha-suave last:border-0" title={l.erro ?? undefined}>
                    <td className="numero px-2 py-1 text-tinta-2">{l.linha}</td>
                    <td className="px-2 py-1 font-mono">{l.codigo || '—'}</td>
                    <td className="px-2 py-1">{l.descricao ?? <span className="text-tinta-2">{l.erro}</span>}</td>
                    <td className="numero px-2 py-1 text-right text-tinta-2">{formatarCusto(l.custoAtual)}</td>
                    <td className="numero px-2 py-1 text-right font-medium">{formatarCusto(l.custoNovo)}</td>
                    {temAcrescimo && (
                      <>
                        <td className="numero px-2 py-1 text-right text-tinta-2">{formatarCusto(l.acrescimoAtual)}</td>
                        <td className="numero px-2 py-1 text-right font-medium">{formatarCusto(l.acrescimoNovo)}</td>
                      </>
                    )}
                    <td className={clsx('px-2 py-1 whitespace-nowrap', ROTULO_STATUS[l.status].classe)}>{ROTULO_STATUS[l.status].texto}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2 border-t border-linha-suave pt-4">
            <Botao variante="principal" disabled={aGravar.length === 0} carregando={gravando} onClick={() => void confirmar()}>
              Gravar {plural(aGravar.length, 'custo', 'custos')}
            </Botao>
          </div>
        </>
      )}
    </div>
  );
}

/** Mesmo acréscimo por unidade para todos os materiais filtrados (ex.: Institucional, R$ 0,29). */
function AcrescimoLote({ codigos, aoConcluir }: { codigos: string[]; aoConcluir: (mensagem: string) => void }) {
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const aplicar = useMutation({
    mutationFn: (valor: number | null) => apiCustos.acrescimoLote(codigos, valor),
    onSuccess: (r, valor) =>
      aoConcluir(
        valor === null
          ? `Acréscimo removido de ${plural(r.alterados, 'material', 'materiais')}.`
          : `Acréscimo de R$ ${formatarCusto(valor)} aplicado em ${plural(r.alterados, 'material', 'materiais')}.`,
      ),
    onError: (e) => setErro(e instanceof ErroApi ? e.message : 'Não foi possível aplicar.'),
  });

  function enviar(remover: boolean) {
    setErro(null);
    if (remover) return aplicar.mutate(null);
    const n = lerNumero(texto);
    if (n === null || Number.isNaN(n) || n < 0) {
      setErro('Informe um valor válido, por exemplo 0,29.');
      return;
    }
    aplicar.mutate(n === 0 ? null : n);
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        enviar(false);
      }}
    >
      <p className="text-tinta-2">
        Valor em R$ somado ao preço unitário <strong className="text-tinta">depois</strong> do cálculo, sem margem nem custos variáveis (ex.:
        embalagem do Institucional). Vale para os <strong className="text-tinta">{plural(codigos.length, 'material filtrado', 'materiais filtrados')}</strong>.
      </p>
      <Campo rotulo="Acréscimo por unidade (R$)" inputMode="decimal" autoFocus value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="0,29" erro={erro ?? undefined} />
      <div className="flex justify-between gap-2 border-t border-linha-suave pt-4">
        <Botao variante="perigo" carregando={aplicar.isPending && aplicar.variables === null} onClick={() => enviar(true)}>
          Remover acréscimo
        </Botao>
        <Botao type="submit" variante="principal" carregando={aplicar.isPending && aplicar.variables !== null}>
          Aplicar em {plural(codigos.length, 'material', 'materiais')}
        </Botao>
      </div>
    </form>
  );
}
